/**
 * Contract File Search API
 * 
 * Wrapper for searching contract files using Elasticsearch
 */
import { v1Api } from '../../../api/generatedClient'
import type { APIContractFileSearchResponse } from '../../../generated-types/models/APIContractFileSearchResponse'
import type { APIContractFileSearchResult } from '../../../generated-types/models/APIContractFileSearchResult'
import type { V1SearchContractsContractFilesRetrieveRequest } from '../../../generated-types/apis/V1Api'
import { createFileInfo } from '../../../utils/functions'
import type { FileInfo } from '../../../utils/types'

export interface ContractFileSearchFilters {
  q: string
  contractId?: number
  requestId?: number
  requestCertificate?: string
  fileName?: string
  storedAt?: string // YYYY-MM-DD
  fileSize?: string // File size (e.g., '10287', '10.05', '10.05 KB', '1.5 MB')
  fields?: string[]
  size?: number
  from?: number
}

export interface ContractFileSearchResponse {
  total: number
  max_score: number | null
  results: FileInfo[]
  page: {
    from: number
    size: number
    has_more: boolean
  }
}

function toContractFileSearchRequest(
  filters: ContractFileSearchFilters
): V1SearchContractsContractFilesRetrieveRequest {
  if (!filters.q || filters.q.trim() === '') {
    throw new Error('Search query (q) is required')
  }

  return {
    q: filters.q,
    fields: filters.fields && filters.fields.length > 0 ? filters.fields.join(',') : undefined,
    size: filters.size,
    from: filters.from,
    contractId: filters.contractId,
    requestId: filters.requestId,
    requestCertificate: filters.requestCertificate,
    fileName: filters.fileName,
    storedAt: filters.storedAt ? new Date(filters.storedAt) : undefined,
    fileSize: filters.fileSize,
  }
}

/**
 * Search for contract files using Elasticsearch
 * 
 * @param filters - Search filters
 * @returns Contract file search response with results and pagination info
 */
export async function searchContractFiles(
  filters: ContractFileSearchFilters
): Promise<ContractFileSearchResponse> {
  const response: APIContractFileSearchResponse = await v1Api.v1SearchContractsContractFilesRetrieve(
    toContractFileSearchRequest(filters)
  )

  // Map results to FileInfo
  const fileInfos = response.results.map((result) => {
    const fileInfo: Record<string, unknown> = {
      self: `/v1/contract/contracts/${result.contractId ?? 'unknown'}/files/${result.id}/`,
      name: result.fileName ?? '',
      key: result.key ?? '',
      size: result.fileSize ?? 0,
      stored_at: result.storedAt?.toISOString(),
      last_modified_at: result.lastModifiedAt?.toISOString(),
    }

    if (result.contractId) {
      fileInfo.contract = `/v1/contract/contracts/${result.contractId}/`
    }

    return createFileInfo(fileInfo)
  })

  return {
    total: response.total,
    max_score: response.maxScore,
    results: fileInfos,
    page: {
      from: response.page.fromValue,
      size: response.page.size,
      has_more: response.page.hasMore,
    },
  }
}
